import React, {createContext, useContext, useMemo} from 'react';
import {Box, Divider, Tab, Tabs} from '@mui/material';
import type {AuditPeriodSlice} from '@/utils/auditPeriodSplit';

export type WpPeriodTabsUiValue = {
    periods: AuditPeriodSlice[];
    activeKey: string;
    onChange: (key: string) => void;
} | null;

const WpPeriodTabsUiContext = createContext<WpPeriodTabsUiValue>(null);

export function WpPeriodTabsUiProvider({
    value,
    children,
}: {
    value: WpPeriodTabsUiValue;
    children: React.ReactNode;
}) {
    return (
        <WpPeriodTabsUiContext.Provider value={value}>
            {children}
        </WpPeriodTabsUiContext.Provider>
    );
}

export function useWpPeriodTabsUi(): WpPeriodTabsUiValue {
    return useContext(WpPeriodTabsUiContext);
}

/** MUI Period Tabs bar for BS whitelist WP (Phase 5). Renders nothing when context absent (#20). */
export function WpPeriodTabsBar({sx}: {sx?: object}) {
    const ui = useWpPeriodTabsUi();
    if (!ui || !ui.periods || ui.periods.length < 2) return null;

    return (
        <Box sx={{width: '100%', mt: 1, mb: 0.5, ...(sx as object)}}>
            <Tabs
                value={ui.activeKey || ui.periods[0].key}
                onChange={(_, v) => ui.onChange(String(v))}
                variant="scrollable"
                scrollButtons="auto"
                sx={{minHeight: 36, '& .MuiTab-root': {minHeight: 36, py: 0}}}
            >
                {ui.periods.map((s) => (
                    <Tab
                        key={s.key}
                        value={s.key}
                        label={s.yearOfAssessment || s.label || `${s.periodFrom} – ${s.periodTo}`}
                    />
                ))}
            </Tabs>
        </Box>
    );
}

export function useActivePeriodSlice(periods: AuditPeriodSlice[] | undefined | null): AuditPeriodSlice | null {
    const ui = useWpPeriodTabsUi();
    return useMemo(() => {
        if (!periods || periods.length === 0) return null;
        const key = ui?.activeKey || periods[0].key;
        return periods.find((p) => p.key === key) || periods[0];
    }, [periods, ui?.activeKey]);
}

/**
 * Place AFTER WpHeader + WpPerCheckSection (or WpCheckToolbar) and BEFORE period-scoped body.
 * Keeps header / per-check / EpWorkingContent outside the tab switch surface (PPE layout).
 */
export function WpSeamlessPeriodTabsSlot({
    enabled,
    sx,
}: {
    enabled: boolean;
    sx?: object;
}) {
    if (!enabled) return null;
    return (
        <Box sx={{width: '100%', maxWidth: '100%', minWidth: 0, flexShrink: 0, ...(sx as object)}}>
            <WpPeriodTabsBar sx={{mt: 0, mb: 1}}/>
            <Divider sx={{mb: 2}}/>
        </Box>
    );
}
